<script setup>
import { ref, onMounted, computed, watch, reactive, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { useNotificationStore } from '@/stores/notification';
import { TheMask } from 'vue-the-mask';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const notificationStore = useNotificationStore();

const isEditMode = ref(false);
const formRef = ref(null); // Reference to the form
const submitAttempted = ref(false);
const showPassword = ref(false); // Add this line to track password visibility

// Make form reactive to better track changes including switches
const form = reactive({
  nome: '',
  email: '',
  foto: '',
  situacao: false,
  datanascimento: '',
  dataadmissao: '',
  datademissao: '',
  cpf: '',
  freela: false,
  celular1: '',
  celular2: '',
  endereco: '',
  cnpj: '',
  razaosocial: '',
  subdominio: '',
  site_parceiro: false,
  perfil: '',
  senha: '',
});

const loading = ref(false);
const error = ref('');
const valid = ref(false); // Changed back to false for proper validation

const nomeRules = [(v) => !!v || 'Nome é obrigatório'];
const emailRules = [
  (v) => !!v || 'E-mail é obrigatório',
  (v) => /.+@.+\..+/.test(v) || 'E-mail precisa ser válido',
];
const perfilRules = [(v) => !!v || 'Perfil é obrigatório'];
const senhaRules = [(v) => isEditMode.value || !!v || 'Senha é obrigatória'];

// Validation helper
const validateForm = () => {
  // Check required fields explicitly
  if (!form.nome) {
    notificationStore.showNotification('Nome é obrigatório', 'error');
    return false;
  }
  if (!form.email) {
    notificationStore.showNotification('Email é obrigatório', 'error');
    return false;
  }
  if (!form.perfil) {
    notificationStore.showNotification('Perfil é obrigatório', 'error');
    return false;
  }
  if (!isEditMode.value && !form.senha) {
    notificationStore.showNotification('Senha é obrigatória para novos usuários', 'error');
    return false;
  }
  
  // Validate email format
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    notificationStore.showNotification('Email precisa ser válido', 'error');
    return false;
  }
  
  return true;
};

onMounted(async () => {
  await userStore.fetchPerfis();
  const userId = route.params.id;
  if (userId) {
    isEditMode.value = true;
    loading.value = true;
    try {
      await userStore.fetchUsuario(userId);
      await userStore.fetchPaginas(userId);
      const user = userStore.usuario;
      
      // Update form with user data
      Object.assign(form, {
        nome: user.nome,
        email: user.email,
        foto: user.foto,
        situacao: user.situacao,
        datanascimento: user.datanascimento,
        dataadmissao: user.dataadmissao,
        datademissao: user.datademissao,
        cpf: user.cpf,
        freela: user.freela,
        celular1: user.celular1,
        celular2: user.celular2,
        endereco: user.endereco,
        cnpj: user.cnpj,
        razaosocial: user.razaosocial,
        subdominio: user.subdominio,
        site_parceiro: user.site_parceiro,
        perfil: user.perfil.id,
        senha: '',
      });
    } catch (err) {
      error.value = 'Erro ao carregar dados do usuário';
    } finally {
      loading.value = false;
    }
  }
});

watch(() => route.params.id, async (newId) => {
  if (newId) {
    isEditMode.value = true;
    loading.value = true;
    try {
      await userStore.fetchUsuario(newId);
      await userStore.fetchPaginas(newId);
      const user = userStore.usuario;
      
      // Update form with user data
      Object.assign(form, {
        nome: user.nome,
        email: user.email,
        foto: user.foto,
        situacao: user.situacao,
        datanascimento: user.datanascimento,
        dataadmissao: user.dataadmissao,
        datademissao: user.datademissao,
        cpf: user.cpf,
        freela: user.freela,
        celular1: user.celular1,
        celular2: user.celular2,
        endereco: user.endereco,
        cnpj: user.cnpj,
        razaosocial: user.razaosocial,
        subdominio: user.subdominio,
        site_parceiro: user.site_parceiro,
        perfil: user.perfil.id,
        senha: '',
      });
    } catch (err) {
      error.value = 'Erro ao carregar dados do usuário';
    } finally {
      loading.value = false;
    }
  }
});

const handleSubmit = async () => {
  submitAttempted.value = true;
  
  // First check basic form validation
  if (!validateForm()) {
    return; // Stop submission if validation fails
  }
  
  loading.value = true;
  error.value = '';

  try {
    if (isEditMode.value) {
      await userStore.updateUsuario(route.params.id, form);
      notificationStore.showNotification('Usuário atualizado com sucesso!', 'success');
    } else {
      const newUser = await userStore.createUsuario(form);
      notificationStore.showNotification('Usuário cadastrado com sucesso!', 'success');
      router.push({ name: 'UsuarioForm', params: { id: newUser.id } });
    }
  } catch (err) {
    if (err.response && err.response.status === 422) {
      const errorMessage = err.response.data.message;
      error.value = errorMessage;
      notificationStore.showNotification(errorMessage, 'error');
    } else {
      error.value = err.message || 'Erro ao salvar usuário';
      notificationStore.showNotification(error.value, 'error');
    }
  } finally {
    loading.value = false;
  }
};

const handlePaginaEstadoChange = async (userId, pagina) => {
  try {
    await userStore.updatePaginaEstado(userId, pagina.id, { estado: pagina.estado ? 1 : 0 });
    notificationStore.showNotification('Estado da página atualizado com sucesso!', 'success');
  } catch (err) {
    notificationStore.showNotification('Erro ao atualizar estado da página', 'error');
  }
};

const handleBack = () => {
  userStore.clearUsuario();
  router.push('/usuarios');
};

const paginas = computed(() => {
  return userStore.paginas.map(pagina => ({
    ...pagina,
    estado: pagina.estado === 1,
  }));
});
</script>

<template>
  <v-card class="elevation-12">
    <v-card-title class="text-left">
      <h1 class="text-h5 mt-4">{{ isEditMode ? 'Editar Usuário' : 'Cadastrar Usuário' }}</h1>
    </v-card-title>

    <v-card-text>
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        closable
        class="mb-4"
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <v-form ref="formRef" v-model="valid" @submit.prevent>
        <v-row>
          <!-- <v-col cols="12" md="12">
            <v-text-field hide-details="auto" v-model="form.foto" label="Foto"></v-text-field>
          </v-col> -->

          <v-col cols="12" md="6">
            <v-text-field hide-details="auto" v-model="form.nome" :rules="nomeRules" label="Nome" required></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field hide-details="auto" v-model="form.endereco" label="Endereço completo"></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="form.perfil"
              :items="userStore.perfis"
              item-title="descricao"
              item-value="id"
              :rules="perfilRules"
              label="Perfil"
              required
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field hide-details="auto" v-model="form.email" :rules="emailRules" label="E-mail" required></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.celular1"
              label="Celular Pessoal"
              v-mask="'(##) #####-####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.celular2"
              label="Celular de Contato"
              v-mask="'(##) #####-####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.cpf"
              label="CPF"
              v-mask="'###.###.###-##'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.dataadmissao"
              label="Data de Entrada"
              v-mask="'##/##/####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.datademissao"
              label="Data de Saída"
              v-mask="'##/##/####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.datanascimento"
              label="Data de Nascimento"
              v-mask="'##/##/####'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.cnpj"
              label="CNPJ"
              v-mask="'##.###.###/####-##'"
              hide-details="auto"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field hide-details="auto" v-model="form.razaosocial" label="Razão Social"></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field hide-details="auto" v-model="form.subdominio" label="Subdomínio"></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.senha"
              label="Senha"
              :type="showPassword ? 'text' : 'password'"
              :rules="senhaRules"
              hide-details="auto"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-switch 
              hide-details="auto" 
              v-model="form.situacao" 
              label="Ativo" 
              color="success"
            ></v-switch>
          </v-col>

          <v-col cols="12" md="4">
            <v-switch 
              hide-details="auto" 
              v-model="form.freela" 
              label="Freelancer" 
              color="success"
            ></v-switch>
          </v-col>

          <v-col cols="12" md="4">
            <v-switch 
              hide-details="auto" 
              v-model="form.site_parceiro" 
              label="Site Parceiro" 
              color="success"
            ></v-switch>
          </v-col>

          <v-col cols="12" md="12" v-if="isEditMode">
            <v-divider></v-divider>

            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-title>Gerenciar acesso a páginas</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row no-gutters>
                    <v-data-table 
                      :headers="[
                        { title: 'Estado', value: 'estado'},
                        { title: 'Página', value: 'name' },
                      ]"
                      :items="paginas"
                    >
                      <template #item="{ item }">
                        <tr>
                          <td>
                            <v-switch hide-details="auto" v-model="item.estado" label="" color="success" @change="handlePaginaEstadoChange(route.params.id, item)"></v-switch></td>
                          <td>{{ item.name }}</td>
                        </tr>
                      </template>
                    </v-data-table>
                  </v-row>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <v-btn color="primary" block size="large" @click="handleSubmit" :loading="loading">
              {{ isEditMode ? 'Salvar' : 'Cadastrar' }}
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn variant="text" color="primary" block @click="handleBack()">Voltar</v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>